// HigherRolesTree.js
import React, { useEffect, useRef, useMemo, useCallback } from 'react';

const mapToApexTree = (node, startIndex = 1) => {
  let currentIndex = startIndex;

  const processNode = (node) => {
    const nodeIndex = currentIndex++;
    const nodeId = `node-${node.id}`;
    return {
      id: nodeId,
      data: {
        id: nodeId,
        name: node.role,
        index: node.id,
        promotionLevel: node.promotionLevel,
        imageURL: node.data?.imageURL,
        expectedSalaryIncreasePercent: node.expectedSalaryIncreasePercent,
        borderColor: node.options?.nodeBGColor || '#E3E6F0',
      },
      children: node.children ? node.children.map(processNode) : [],
    };
  };

  return processNode(node);
};

const HigherRolesTree = React.memo(({ higherRolesWithPromotions }) => {
  const containerRef = useRef(null);
  const treeInstanceRef = useRef(null);

  // Memoize the tree data to prevent unnecessary recalculations
  const treeData = useMemo(() => {
    if (!higherRolesWithPromotions) {
      return null;
    }

    // Handle both array and object formats
    let rootNode;
    if (Array.isArray(higherRolesWithPromotions)) {
      if (higherRolesWithPromotions.length === 0) {
        return null;
      }
      // If it's an array, take the first element as the root node
      rootNode = higherRolesWithPromotions[0];
    } else if (typeof higherRolesWithPromotions === 'object') {
      // If it's already an object, use it directly as the root node
      rootNode = higherRolesWithPromotions;
    } else {
      return null;
    }

    return mapToApexTree(rootNode);
  }, [higherRolesWithPromotions]);

  // Memoize the options to prevent recreation on every render
  const treeOptions = useMemo(() => ({
    contentKey: 'data',
    width: '100%',
    height: 'auto',
    nodeWidth: 320,
    nodeHeight: 120,
    childrenSpacing: 70,
    siblingSpacing: 30,
    direction: 'bottom',
    nodeTemplate: (content) => {
      return `
    <div class="career-node" style="display: flex; flex-direction: column; height: 100%; border: 2px solid ${content.borderColor}; border-radius: 12px; padding: 8px; box-sizing: border-box; font-family: Arial;">
      <div class="node-header" style="display: flex; align-items: center; margin-bottom: 8px;">
        <div class="node-number" style="font-weight: bold; font-size: 16px;">${content.index}</div>
        <div style="flex: 1; margin-left: 12px;">
          <div class="node-title" style="font-size: 14px; font-weight: bold;">${content.name}</div>
          <div class="node-level" style="font-size: 12px; color: #666;">${content.promotionLevel || 'ENTRY'}</div>
        </div>
      </div>
      <div class="salary-info" style="margin-top: auto; font-size: 13px; color: #333;">
        💰 ${content.expectedSalaryIncreasePercent || 'N/A'} increase
      </div>
    </div>
  `;
    },
    nodeStyle: `
      border-radius: 12px;
      overflow: hidden;
      transition: all 0.3s ease;
    `,
    canvasStyle: `
      background: rgba(255, 255, 255, 0.95);
      border-radius: 24px;        
      border: 1px solid #E3E6F0;
      height: 600px;
      backdrop-filter: blur(10px);
    `
  }), []);

  // Cleanup function to destroy tree instance
  const cleanupTree = useCallback(() => {
    if (treeInstanceRef.current) {
      try {
        // Clear the container
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }
        treeInstanceRef.current = null;
      } catch (error) {
        console.warn('Error cleaning up tree:', error);
      }
    }
  }, []);

  useEffect(() => {
    // Cleanup previous tree instance
    cleanupTree();

    // Only render if we have data and ApexTree is available
    if (treeData && window.ApexTree && containerRef.current) {
      try {
        // Create new tree instance
        treeInstanceRef.current = new window.ApexTree(containerRef.current, treeOptions);
        treeInstanceRef.current.render(treeData);
      } catch (error) {
        console.error('Error rendering tree:', error);
      }
    }

    // Cleanup on unmount or when dependencies change
    return cleanupTree;
  }, [treeData, treeOptions, cleanupTree]);

  // Don't render if no data
  if (!higherRolesWithPromotions) {
    return null;
  }

  // Check if data is valid (either non-empty array or non-empty object)
  const isValidData =
    (Array.isArray(higherRolesWithPromotions) && higherRolesWithPromotions.length > 0) ||
    (typeof higherRolesWithPromotions === 'object' && !Array.isArray(higherRolesWithPromotions) && Object.keys(higherRolesWithPromotions).length > 0);

  if (!isValidData) {
    return null;
  }

  return (
    <div>
      <div id="svg-tree" ref={containerRef} style={{ width: '100%' }} />
    </div>
  );
});

// Add display name for debugging
HigherRolesTree.displayName = 'HigherRolesTree';

export default HigherRolesTree;
